import { FC } from "react";
import { cx } from "shared/utils";
import { Applicant, Application } from "types/application";
import { VerificationStatus } from "shared/types";
import { Verification } from "views/applications-id/components/ApplicantsTable/types";
import {
  AlertCircle,
  CursorClick01,
  InfoCircle,
  Loading02,
  Minus,
  SVGComponentProps
} from "untitledui-js-base";
import {
  ReportState,
  VerificationStateHandler
} from "shared/utils/verification-state-handler";

/*
 * A credit report "no-hit" (the bureau found no credit file for the applicant)
 * comes back as finished_with_warnings, which reads as a problem with the
 * applicant. It is not a verification status, so it is modelled separately.
 */
export const NO_CREDIT_FILE = "no_credit_file";
type NoCreditFileState = typeof NO_CREDIT_FILE;

type BadgeVariant = "neutral" | "primary" | "danger" | "warning" | "info";

interface BadgeConfig {
  title: string;
  variant: BadgeVariant;
  icon?: FC<SVGComponentProps>;
  iconClassName?: string;
}

const variantStyles: Record<BadgeVariant, { container: string; icon: string }> =
  {
    neutral: {
      container: "bg-background-900 text-content-200",
      icon: "stroke-content-200"
    },
    primary: {
      container: "bg-primary-20 text-primary-500",
      icon: "stroke-primary-500"
    },
    danger: {
      container: "bg-danger-50 text-danger-500",
      icon: "stroke-danger-500"
    },
    warning: {
      container: "bg-warning-50 text-warning-700",
      icon: "stroke-warning-700"
    },
    info: {
      container: "bg-info-50 text-info-700",
      icon: "stroke-info-700"
    }
  };

const getBadgeConfig = (
  state: ReportState | VerificationStatus | NoCreditFileState
): BadgeConfig | null => {
  switch (state) {
    case NO_CREDIT_FILE:
      return {
        title: "No credit file found",
        icon: InfoCircle,
        variant: "info"
      };

    case "idv_override_required":
      return {
        title: "Action required",
        icon: AlertCircle,
        variant: "primary",
        iconClassName: "size-5"
      };

    case "warnings_override_required":
      return {
        title: "Paused",
        icon: AlertCircle,
        variant: "primary",
        iconClassName: "size-5"
      };

    case "manual_pending":
      return {
        title: "Awaiting manual run",
        icon: CursorClick01,
        variant: "primary",
        iconClassName: "size-5"
      };

    case "idv_bypassed_not_run":
      return { title: "Not run", icon: Minus, variant: "neutral" };

    case "conditional_skipped":
    case "skipped":
      return { title: "Wasn't needed", icon: Minus, variant: "neutral" };

    case "started":
      return { title: "In progress", icon: Loading02, variant: "neutral" };

    case "submitted_in_progress":
    case "rerun_in_progress":
    case "idv_retrying":
    case "processing":
      return { title: "Processing", icon: Loading02, variant: "neutral" };

    case "conditional_pending":
    case "pending":
      return { title: "Not started", icon: Minus, variant: "neutral" };

    case "canceled":
      return { title: "Canceled", icon: Minus, variant: "neutral" };

    case "failed":
      return {
        title: "Issues with report",
        icon: AlertCircle,
        variant: "danger"
      };

    case "finished_with_warnings":
      return {
        title: "Warnings in report",
        icon: AlertCircle,
        variant: "warning"
      };

    case "email_sent":
      return {
        title: "Email sent",
        icon: Loading02,
        variant: "neutral"
      };

    default:
      return null;
  }
};

const BadgeWrapper = ({
  variant,
  icon: IconComponent,
  title,
  iconClassName
}: BadgeConfig) => {
  const styles = variantStyles[variant];

  return (
    <div
      className={cx(
        "absolute -top-0 right-0 rounded-bl-lg px-2 py-1.5 border border-t-0 flex items-center gap-2",
        styles.container
      )}
    >
      {IconComponent && (
        <IconComponent
          className={cx("w-3.5 h-3.5", styles.icon, iconClassName)}
        />
      )}
      <p className="text-xs font-medium">{title}</p>
    </div>
  );
};

export const ReportStatusBadge = ({
  verification,
  applicant,
  application,
  overrideStatus
}: {
  verification?: Verification;
  applicant?: Applicant;
  application?: Application;
  overrideStatus?: VerificationStatus | NoCreditFileState;
}) => {
  const { getReportState, isCriminalApprovalRequired } =
    new VerificationStateHandler({
      verification,
      applicant,
      application
    });

  const criminalApprovalRequired = isCriminalApprovalRequired();
  if (criminalApprovalRequired)
    return <BadgeWrapper variant="neutral" title="Needs conditional offer" />;

  const reportState = getReportState();
  const config = getBadgeConfig(overrideStatus ?? reportState);

  if (!config) return null;

  return <BadgeWrapper {...config} />;
};
